import React from 'react';
import { Panel, PanelHeaderTemplateOptions, PanelProps } from 'primereact/panel';
import ThemeButtonLink from '@Components/Form/ThemeButtonLink/ThemeButtonLink';
import Language from '@Components/Language';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { AutoComplete } from 'primereact/autocomplete';
import { IData } from '@Types/IData';
import { Calendar } from 'primereact/calendar';
import { ColumnFilterElementTemplateOptions } from 'primereact/column';
import { ItemPickList } from '@Pages/Portal/FormGroups/FormGroup';
import {ActionButtonTemplate} from "@Components/DataTable/ActionButtonTemplate";

export const preservationOptions = {
  preserveState: true,
  preserveScroll: true,
};

export const blurActiveElement = () => {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
};

// Record Status

// Document Policy
export const documentTypes: StatusData[] = [
  {
    value: 'document',
    label: Language.get('Document'),
  },
  {
    value: 'policy',
    label: Language.get('Policy'),
  },
];

// Yes no drop down options
export const yesNoSelections: StatusData[] = [
  {
    value: 'yes',
    label: Language.get('Yes'),
  },
  {
    value: 'no',
    label: Language.get('No'),
  },
];

export const certificateStatusSelections: StatusData[] = [
  {
    value: 'pending',
    label: Language.get('Pending'),
  },
  {
    value: 'approved',
    label: Language.get('Approved'),
  },
  {
    value: 'rejected',
    label: Language.get('Rejected'),
  },

];

export const trueFalseSelections: TrueFalseStatusData[] = [
  {
    value: true,
    label: Language.get('Yes'),
  },
  {
    value: false,
    label: Language.get('No'),
  },
];

// convert string to date object
export const convertToDate = (date: string) => (date ? new Date(date) : undefined);

export const recordStatusOptions: StatusData[] = [
  { label: Language.get('Active'), value: 'active' },
  { label: Language.get('Archived'), value: 'archive' },
  { label: Language.get('Disabled'), value: 'disable' },
];

export const getRecordStatusOptions = (tableType: string): StatusData[] => {
  switch (tableType) {
    case 'tableAccessGroup':
      return [
        { label: Language.get('Active'), value: 'active' },
        { label: Language.get('Archived'), value: 'archive' },
      ];
    // Add more cases as needed for different tables
    default:
      return recordStatusOptions; // Default options
  }
};

export const statusRowFilterTemplate = (options: {
  field: string;
  value: string;
  filterCallback: (arg0: any, arg1: any) => void;
  filterApplyCallback: (value: string) => void;
  index: any;
  tableType?: string;
  showClear?: boolean;
}) => {
  const item = (option: StatusData) => {
    return <span className={`record-status status-${option.value.toLowerCase()}`}>{Language.get(option.label)}</span>;
  };

  const optionsList = [
    { label: Language.get('All'), value: 'all' },
    ...getRecordStatusOptions(options.tableType || 'default'),
  ];

  return (
    <Dropdown
      value={options.value}
      options={optionsList}
      onChange={(e) => options.filterCallback(e.value, options.index)}
      itemTemplate={item}
      placeholder={Language.get('Select Status')}
      className="p-column-filter"
      showClear={options?.showClear ?? true}
      style={{ minWidth: '12rem' }}
    />
  );
};

export const companyIdFilterTemplate = (
  options: {
    value: string;
    filterCallback: (arg0: any, arg1: any) => void;
    filterApplyCallback: (value: string) => void;
    index: any;
  },
  companies: IData[]
) => (
  <Dropdown
    optionLabel="name"
    optionValue="record_id"
    options={companies}
    value={options.value}
    filter
    placeholder={Language.get('Company')}
    onChange={(e) => {
      options.filterApplyCallback(e.value);
    }}
    // itemTemplate={item}
    className="p-column-filter"
    showClear
    style={{ minWidth: '12rem' }}
  />
);

export const dropdownFilterTemplate = (
  options: {
    field: string;
    value: any;
    filterCallback: (arg0: any, arg1: any) => void;
    filterApplyCallback: (value: any) => void;
    index: any;
  },
  props: {
    options: IData[];
    placeholder: string;
    optionValue?: string;
    optionLabel?: string;
    multiple?: boolean;
  }
) => {
  if (props.multiple) {
    return (
      <MultiSelect
        optionLabel={props.optionLabel || 'name'}
        optionValue={props.optionValue || 'remote_id'}
        options={props.options}
        value={options.value ?? []}
        filter
        placeholder={props.placeholder}
        onChange={(e) => {
          options.filterCallback(e.value, options.index);
        }}
        className="p-column-filter"
        style={{ minWidth: '12rem' }}
        maxSelectedLabels={2}
      />
    );
  }

  return (
    <Dropdown
      optionLabel={props.optionLabel || 'name'}
      optionValue={props.optionValue || 'remote_id'}
      options={props.options}
      value={options.value}
      filter
      placeholder={props.placeholder}
      onChange={(e) => {
        options.filterCallback(e.value, options.index);
      }}
      className="p-column-filter"
      showClear
      style={{ minWidth: '12rem' }}
    />
  );
};

export const dateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
  return (
    <Calendar
      value={options.value}
      onChange={(e) => options.filterCallback(e.value, options.index)}
      dateFormat="dd/mm/yy"
      selectionMode="range"
      showButtonBar
      showIcon
      readOnlyInput
      showOnFocus={false}
      hideOnRangeSelection
      placeholder={Language.get('Date From - To')}
    />
  );
};

export const documentTypeFilterTemplate = (options: {
  value: string;
  filterCallback: (arg0: any, arg1: any) => void;
  filterApplyCallback: (value: string) => void;
  index: any;
}) => (
  <Dropdown
    value={options.value}
    options={documentTypes}
    onChange={(e) => options.filterApplyCallback(e.value)}
    placeholder={Language.get('Select Document Type')}
    className="p-column-filter"
    showClear
    style={{ minWidth: '12rem' }}
  />
);

export const yesNoFilterTemplate = (
  options: {
    field: string;
    value: string;
    filterCallback: (arg0: any, arg1: any) => void;
    filterApplyCallback: (value: string) => void;
    index: any;
  },
  placeholder: string
) => (
  <Dropdown
    value={options.value}
    options={yesNoSelections}
    onChange={(e) => options.filterCallback(e.value, options.index)}
    placeholder={placeholder}
    className="p-column-filter"
    showClear
    style={{ minWidth: '12rem' }}
  />
);

export const certificateStatusFilterTemplate = (
  options: {
    field: string;
    value: string;
    filterCallback: (arg0: any, arg1: any) => void;
    filterApplyCallback: (value: string) => void;
    index: any;
  },
  placeholder: string
) => (
  <Dropdown
    value={options.value}
    options={certificateStatusSelections}
    onChange={(e) => options.filterCallback(e.value, options.index)}
    placeholder={placeholder}
    className="p-column-filter"
    showClear
    style={{ minWidth: '12rem' }}
  />
);

export const editRowButton = (params: IData, routeName: string, type?:  "view" | "edit" | "create" | "upload" | "delete" | undefined, label?: string, button_type?: 'button' | 'submit') => {
  return (
    <div className="flex justify-content-start">
      <ActionButtonTemplate edit_route={route(routeName, params)} type={type} label={label} button_type={button_type} />
    </div>
  );
};
export const actionRowButton = (params: IData, routeName: string, action?: 'view' | 'edit' | undefined, button_type?: 'button' | 'submit') => {
  return (
    <div className="flex justify-content-start">
      <ActionButtonTemplate edit_route={route(routeName, params)} type={action} button_type={button_type} />
    </div>
  );
};


export const formatNumber = (num: number) => {
  if (Number(num) >= 1000) {
    return `${(Number(num) / 1000).toFixed(0)} km`;
  }

  return `${Number(num).toString()} m`;
};

export const fixed2 = (value: unknown, fallback: string = '-'): string => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(2) : fallback;
};

export const fullName = (row: { firstname?: string; lastname?: string }): string =>
  [row?.firstname, row?.lastname].filter(Boolean).join(' ') || '-';

export const itemPickOnlyTemplate = (item: ItemPickList, prop: 'title' | 'label' | 'name' = 'title') => (
  <div className="flex flex-wrap p-2 align-items-center gap-3">
    <div className="flex-1 flex flex-column gap-2">
      <span>{item?.[prop]}</span>
    </div>
  </div>
);

export const handleDownload = (fileUrl: string | undefined) => {
  if (fileUrl) {
    // Create an anchor element and set the download attribute
    const link: any = document.createElement('a');
    link.href = fileUrl;
    link.target = '_blank';
    link.download = fileUrl.split('/').pop();
    link.click();
  }
};


export function hexToRgba(hex: string, alpha = 0.3) {
  // Ensure it's a valid hex format
  if (!/^#[0-9A-F]{6}$/i.test(hex)) return hex;

  // Remove the '#' character
  const hexColor = hex.slice(1);

  // Convert hex to RGB
  const r = parseInt(hexColor.substring(0, 2), 16);
  const g = parseInt(hexColor.substring(2, 4), 16);
  const b = parseInt(hexColor.substring(4, 6), 16);

  // Return the rgba string with the desired alpha
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function rgbToHex(rgb: string): string {
  // Match rgb(r, g, b) or rgba(r, g, b, a) format
  const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return "";

  const r = parseInt(match[1]);
  const g = parseInt(match[2]);
  const b = parseInt(match[3]);

  const toHex = (n: number) => n.toString(16).padStart(2, '0').toUpperCase();
  return toHex(r) + toHex(g) + toHex(b);
}
export const stableStopId = (lat: number, lng: number, label: string): string => {
  const seed = `${(lat ?? 0).toFixed(6)}|${(lng ?? 0).toFixed(6)}|${label}`;
  const hash32 = (init: number) => {
    let acc = init;
    for (let i = 0; i < seed.length; i++) {
      acc = Math.imul(acc ^ seed.charCodeAt(i), 16777619);
    }
    const unsigned = acc < 0 ? acc + 0x100000000 : acc;
    return unsigned.toString(16).padStart(8, '0');
  };
  return [0x811c9dc5, 0x01000193, 0xdeadbeef].map(hash32).join('');
};

// A hash followed by 3 or 6 hex digits, e.g. "#abc" or "#FF0000".
export const HEX_COLOR_REGEX = /^#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

export const isHexColor = (v: string): boolean => HEX_COLOR_REGEX.test((v ?? '').trim());

export const stripHash = (v: string) => (v || "").replace("#", "");

export const ensureHash = (v: string) => (v.startsWith("#") ? v : `#${v}`);

export const normalizeHex = (v: string): string => {
  if (v && v.startsWith('rgb')) {
    return rgbToHex(v);
  }

  return stripHash(v).toUpperCase().replace(/[^0-9A-F]/g, "").slice(0, 6);
};

export const toDisplayHex = (v: string): string => {
  const normalized = normalizeHex(v);

  return normalized && normalized.length === 6 ? `#${normalized}` : "#000000";
};

const IMAGE_EXTENSIONS = [
  '.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg', '.avif', '.ico',
] as const;

// Strip query string / hash so "photo.jpg?w=200#x" still matches.
function pathPart(value: string): string {
  return value.split(/[?#]/, 1)[0].toLowerCase();
}

function hasImageExtension(value: string): boolean {
  return IMAGE_EXTENSIONS.some((ext) => pathPart(value).endsWith(ext));
}

function hasAnyExtension(value: string): boolean {
  return /\.[a-z0-9]+$/.test(pathPart(value));
}


export function isImageFile(src: unknown, fileName?: string): boolean {
  if (typeof fileName === 'string' && fileName) {
    if (hasImageExtension(fileName)) return true;
    if (hasAnyExtension(fileName)) return false; // has a known non-image extension
  }

  if (typeof src !== 'string') return false;

  const lower = src.toLowerCase();
  return (
    hasImageExtension(src) ||
    lower.startsWith('blob:') ||
    lower.startsWith('data:image/')
  );
}

export const getTargetFields = (
  dataTargetSource: { record_id: string, record_status: string }[],
  formAccessObjects: string[] | undefined,
  object_type: string) => {
  if (!dataTargetSource) {
    return { addedData: [], removedData: [] };
  }

  // Find added forms
  const addedForms = dataTargetSource.filter(
    (item: { record_id: string, record_status: string}) => !formAccessObjects?.includes(item.record_id)
  );

  const addedData = addedForms.map((item: { record_id: string, record_status: string }) => ({
    collection_object_id: item.record_id,
    object_type: object_type,
    status: item.record_status, // 'active' or current status
  }));

  // Find removed forms
  const removedForms = formAccessObjects?.filter(
    (recordId: string) =>
      !dataTargetSource.some((item: { record_id: string}) => item.record_id === recordId)
  );

  const removedData = (removedForms ?? []).map((recordId: string) => ({
    collection_object_id: recordId,
    object_type: object_type,
    status: 'deactive', // Mark as deactivated
  }));

  return { addedData, removedData }; // ✅ Now returning correct data!
};

export const tryParseLatLng = (text: string): { lat: number; lng: number } | null => {
  const latlng = text.split(',');
  if (latlng.length !== 2) return null;

  const lat = Number(latlng[0].trim());
  const lng = Number(latlng[1].trim());

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  if (lat === 0 && lng === 0) return null; // reject 0,0

  return { lat, lng };
};


export  const valueStyle = "text-md text-color";
export  const labelStyle = "mb-2 font-bold";

type FieldProps = {
  labelKey?: string;
  label?: any;
  value?: any;
  wrapperClass?: string;
};
export const InfoField = ({ labelKey, value, label, wrapperClass = responsiveField }: FieldProps) => (
  <div className={wrapperClass}>
    <p className={labelStyle}>{labelKey ? Language.get(labelKey) : label}</p>
    <span className={valueStyle}>{value ?? '-'}</span>
  </div>
);
export const leftColumn = 'col-12';
export const rightColumn = 'col-12';

export const responsiveField = "col-12 xl:col-4 xxl:col-3 mb-4"
export const responsiveFieldNoMb = "col-12 xl:col-4 xxl:col-3"
export const responsiveField4 = "col-12 md:col-4 mb-4"
export const responsiveField6 = "col-12 md:col-6 mb-4"
export const responsiveField6NoMb = "col-12 md:col-6"
export const responsiveField8 = "col-12 md:col-8 mb-4"
export const responsiveField8NoMb = "col-12 md:col-8"
export const responsiveField12 = "col-12 md:col-12 mb-4"

export const acceptedTypes = '.jpg,.jpeg,.png,.gif,.webp'

export const sectionPanelPt = {
  root: { className: 'mb-4 border-1 border-200 border-round-lg shadow-none overflow-hidden' },
  header: { className: 'flex justify-content-between align-items-center px-3 py-3 surface-0' },
  title: { className: 'font-bold text-sm' },
  icons: { className: 'ml-2' },
  toggler: { className: 'p-0 w-auto h-auto border-none surface-0 text-700' },
  content: { className: 'px-3 py-3 border-top-1 border-200' },
};

export const SectionPanel = ({ header, toggleable, ...props }: PanelProps) => {
  const headerTemplate = toggleable
    ? (options: PanelHeaderTemplateOptions) => (
        <div className="flex justify-content-between align-items-center px-3 py-3 surface-0">
          <span className="font-bold text-sm">{header}</span>
          <ThemeButtonLink
            type="button"
            onClick={options.onTogglerClick}
            icon={options.collapsed ? 'pi pi-plus' : 'pi pi-minus'}
            className="p-0 w-auto h-auto"
          />
        </div>
      )
    : undefined;

  return (
    <Panel
      pt={sectionPanelPt}
      transitionOptions={{ timeout: 0 }}
      headerTemplate={headerTemplate}
      header={header}
      toggleable={toggleable}
      {...props}
    />
  );
};

